import { Fragment } from "react";
import { AtSign, Instagram, Mail, MapPin, Phone } from "lucide-react";
import { agruparPorPedido } from "@bookdental/schemas";

export type ItemDoExtratoDocumento = {
  id: string;
  tipo: "SERVICO" | "RETRABALHO" | "ACRESCIMO";
  pedidoId: string;
  pedidoNumero: number | null;
  paciente: string;
  dentista: string | null;
  descricao: string;
  material: string | null;
  dentes: string | null;
  /** "AAAA-MM-DD" */
  data: string;
  quantidade: number;
  valorCents: number;
};

export type ExtratoParaDocumento = {
  /** Nulo no extrato simples (Cadastros, grátis): não é emitido, mostra o período. */
  numero: number | null;
  /** "AAAA-MM" */
  rotulo: string;
  periodo?: { desde: string; ate: string } | null;
  vencimento: string | null;
  status: "EMITIDO" | "CANCELADO";
  destinatarioNome: string;
  destinatarioDocumento: string | null;
  destinatarioEndereco: string | null;
  totalCents: number;
  cabecalho: {
    clinica?: string | null;
    whatsapp?: string | null;
    email?: string | null;
    instagram?: string | null;
    formasDePagamento?: string | null;
    mostrarDentista?: boolean;
    mostrarDentes?: boolean;
  };
  laboratorio: { nome: string; logoUrl: string | null; cnpj: string | null; telefone: string | null; endereco: string | null };
  itens: ItemDoExtratoDocumento[];
};

const numero = (cents: number) => (cents / 100).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const diaMes = (d: string) => `${d.slice(8, 10)}/${d.slice(5, 7)}`;
const data = (d: string) => `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}`;
const th = "px-2 pb-2 text-[11px] font-bold uppercase tracking-[0.12em] text-slate-600";

/**
 * O extrato de cobrança como documento — no desenho do que o Jun já manda (SB Prótese): logo e
 * "EXTRATO", quem recebe, a tabela QT. / Serviços / Paciente / Data / Valor agrupada por pedido,
 * o total, as formas de pagamento e os contatos do laboratório. Sempre fundo branco: é papel.
 */
export function ExtratoDocumento({ extrato: e }: { extrato: ExtratoParaDocumento }) {
  const { laboratorio: lab, cabecalho: c } = e;
  const grupos = agruparPorPedido(e.itens);
  const contatos = [
    c.whatsapp || lab.telefone ? { icone: Phone, texto: c.whatsapp || lab.telefone } : null,
    c.email ? { icone: Mail, texto: c.email } : null,
    c.instagram ? { icone: Instagram, texto: c.instagram.startsWith("@") ? c.instagram : `@${c.instagram}` } : null,
  ].filter(Boolean) as { icone: typeof AtSign; texto: string }[];

  return (
    <article className="relative mx-auto w-full max-w-3xl bg-white px-2 text-slate-700 print:max-w-none">
      {e.status === "CANCELADO" && (
        <div className="pointer-events-none absolute inset-x-0 top-40 flex justify-center">
          <span className="-rotate-12 rounded-lg border-4 border-red-500 px-6 py-2 text-4xl font-black tracking-widest text-red-500/80">CANCELADO</span>
        </div>
      )}

      <header className="flex items-start justify-between gap-6">
        <div className="flex min-w-0 items-center gap-3">
          {lab.logoUrl && <img src={lab.logoUrl} alt="" className="h-16 w-16 shrink-0 object-contain" />}
          <div className="min-w-0">
            <p className="text-lg font-semibold leading-tight text-slate-800">{lab.nome}</p>
            {lab.cnpj && <p className="text-xs text-slate-500">CNPJ {lab.cnpj}</p>}
          </div>
        </div>
        <div className="shrink-0 text-right">
          <p className="text-4xl font-light tracking-[0.25em] text-slate-700">EXTRATO</p>
          {e.numero != null && <p className="mt-1 text-xs font-semibold tracking-wider text-slate-500">Nº {e.numero}</p>}
        </div>
      </header>

      <dl className="mt-8 space-y-1 text-sm font-bold tracking-wide text-slate-600">
        <div className="flex gap-2">
          <dt>{c.clinica ? "DENTISTA:" : "CLIENTE:"}</dt>
          <dd>{e.destinatarioNome}</dd>
        </div>
        {c.clinica && (
          <div className="flex gap-2">
            <dt>CLÍNICA:</dt>
            <dd>{c.clinica}</dd>
          </div>
        )}
        {e.destinatarioDocumento && (
          <div className="flex gap-2">
            <dt>CNPJ/CPF:</dt>
            <dd>{e.destinatarioDocumento}</dd>
          </div>
        )}
        {e.periodo ? (
          <div className="flex gap-2">
            <dt>PERÍODO:</dt>
            <dd>{`${data(e.periodo.desde)} a ${data(e.periodo.ate)}`}</dd>
          </div>
        ) : (
          <div className="flex gap-2">
            <dt>MÊS DO EXTRATO:</dt>
            <dd>{`${e.rotulo.slice(5, 7)}/${e.rotulo.slice(0, 4)}`}</dd>
          </div>
        )}
        {e.destinatarioEndereco && (
          <div className="flex gap-2">
            <dt>END.:</dt>
            <dd className="font-semibold">{e.destinatarioEndereco}</dd>
          </div>
        )}
      </dl>

      <table className="mt-8 w-full border-collapse text-sm">
        <thead>
          <tr className="text-left">
            <th className={`${th} w-12 text-center`}>QT.</th>
            <th className={th}>Serviços</th>
            <th className={th}>Paciente</th>
            {c.mostrarDentista && <th className={th}>Dentista</th>}
            <th className={`${th} w-16 text-center`}>Data</th>
            <th className={`${th} w-24 text-right`}>Valor</th>
          </tr>
        </thead>
        <tbody>
          {grupos.map((g) => (
            <Fragment key={g.pedidoId}>
              {g.itens.map((i, n) => (
                <tr key={i.id} className={`break-inside-avoid align-top ${n === 0 ? "border-t border-slate-200" : ""}`}>
                  <td className="px-2 py-1.5 text-center font-bold">{i.tipo === "SERVICO" ? i.quantidade : ""}</td>
                  <td className="px-2 py-1.5 font-semibold uppercase text-slate-700">
                    {i.descricao}
                    {i.material && <span className="font-normal normal-case text-slate-500"> · {i.material}</span>}
                    {c.mostrarDentes && i.dentes && <span className="block text-[11px] font-normal normal-case text-slate-500">Dentes {i.dentes}</span>}
                  </td>
                  <td className="px-2 py-1.5">
                    {n === 0 && (
                      <>
                        {i.paciente}
                        {i.pedidoNumero != null && <span className="block text-[11px] text-slate-400">Pedido {i.pedidoNumero}</span>}
                      </>
                    )}
                  </td>
                  {c.mostrarDentista && <td className="px-2 py-1.5 text-slate-600">{n === 0 ? i.dentista : ""}</td>}
                  <td className="px-2 py-1.5 text-center font-bold">{diaMes(i.data)}</td>
                  <td className="px-2 py-1.5 text-right font-bold tabular-nums">{numero(i.valorCents)}</td>
                </tr>
              ))}
            </Fragment>
          ))}
        </tbody>
      </table>

      <div className="mt-10 flex flex-col-reverse gap-8 sm:flex-row sm:items-end sm:justify-between">
        <ul className="space-y-1 text-xs text-slate-500">
          {contatos.map(({ icone: Icone, texto }) => (
            <li key={texto} className="flex items-center gap-2">
              <Icone className="h-3.5 w-3.5" />
              {texto}
            </li>
          ))}
        </ul>
        <div className="sm:max-w-[55%]">
          <p className="text-lg font-bold tracking-wide text-slate-700">VALOR TOTAL: R$ {numero(e.totalCents)}</p>
          {e.vencimento && <p className="text-xs text-slate-500">Vencimento: {data(e.vencimento)}</p>}
          {c.formasDePagamento && (
            <div className="mt-3 text-sm text-slate-600">
              <p className="text-xs text-slate-500">(formas de pagamento)</p>
              <p className="whitespace-pre-line">{c.formasDePagamento}</p>
            </div>
          )}
        </div>
      </div>

      {lab.endereco && (
        <p className="mt-8 flex items-center justify-center gap-2 border-t border-slate-100 pt-3 text-xs text-slate-500">
          <MapPin className="h-3.5 w-3.5" />
          {lab.endereco}
        </p>
      )}
    </article>
  );
}
